/**
 * Canvas confetti. No dependency — this is ~120 lines and the repo does not add
 * packages for things it can do itself (CLAUDE.md refuses react-router on the
 * same reasoning).
 *
 * Fixed, full-viewport, `pointer-events: none`, so it never eats a tap meant for
 * a game underneath it.
 *
 * REDUCED MOTION. A page-filling particle storm is exactly what
 * `prefers-reduced-motion` exists to suppress. When the user has asked for less
 * motion this renders nothing at all — not a slower version — and the page says
 * so rather than silently doing nothing.
 */
import { useEffect, useRef, type JSX } from 'react'

export interface ConfettiProps {
  /** Ambient fall. */
  active: boolean
  /** Palette to draw from. */
  colors: string[]
  /** Increment to fire a burst from the middle of the screen. */
  burstKey?: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  rotation: number
  spin: number
  color: string
}

/** Enough to read as a party, few enough that a phone stays at 60fps. */
const MAX_PARTICLES = 220
const GRAVITY = 0.12
const DRAG = 0.995

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function makeParticle(x: number, y: number, colors: string[], burst: boolean): Particle {
  const color = colors[Math.floor(Math.random() * colors.length)] ?? '#fff'
  const angle = Math.random() * Math.PI * 2
  const speed = burst ? 4 + Math.random() * 7 : 0
  return {
    x,
    y,
    vx: burst ? Math.cos(angle) * speed : -1 + Math.random() * 2,
    vy: burst ? Math.sin(angle) * speed : 1 + Math.random() * 2.5,
    size: 5 + Math.random() * 7,
    rotation: Math.random() * Math.PI,
    spin: -0.2 + Math.random() * 0.4,
    color,
  }
}

export function Confetti({ active, colors, burstKey = 0 }: ConfettiProps): JSX.Element | null {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const particles = useRef<Particle[]>([])
  const frame = useRef<number>(0)
  // Read inside the effect too, but needed before the first paint to skip mounting.
  const reduced = prefersReducedMotion()

  // Fire a burst when burstKey changes. Kept separate from the render loop so a
  // burst works even while ambient fall is switched off.
  useEffect(() => {
    if (reduced || burstKey === 0) return
    const canvas = canvasRef.current
    if (!canvas) return
    const cx = canvas.width / (window.devicePixelRatio || 1) / 2
    const cy = canvas.height / (window.devicePixelRatio || 1) / 3
    for (let i = 0; i < 90; i++) {
      particles.current.push(makeParticle(cx, cy, colors, true))
    }
  }, [burstKey, colors, reduced])

  useEffect(() => {
    if (reduced) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let width = 0
    let height = 0

    const resize = (): void => {
      const dpr = window.devicePixelRatio || 1
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const tick = (): void => {
      ctx.clearRect(0, 0, width, height)

      if (active && particles.current.length < MAX_PARTICLES) {
        particles.current.push(makeParticle(Math.random() * width, -20, colors, false))
      }

      const next: Particle[] = []
      for (const p of particles.current) {
        p.vy += GRAVITY
        p.vx *= DRAG
        p.vy *= DRAG
        p.x += p.vx
        p.y += p.vy
        p.rotation += p.spin

        // Off the bottom is gone. Off the sides wraps, so a windy burst does not
        // leave a bald patch.
        if (p.y > height + 30) continue
        if (p.x < -30) p.x = width + 30
        if (p.x > width + 30) p.x = -30

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        ctx.restore()
        next.push(p)
      }
      particles.current = next
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frame.current)
      window.removeEventListener('resize', resize)
    }
  }, [active, colors, reduced])

  if (reduced) return null

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  )
}

export default Confetti
